import React, { ReactNode } from "react";
import { AnalyticsPageName, BotType, LanguageNameSpaces } from "@shared/types";
import { OrdersSummaryProps } from "@shared/features/bots/BotForm/common/types";
import {
  ANALYTICS_FIELD_NAMES,
  DCA_BOT_HELP_DESCRIPTIONS,
  TRADING_VIEW_BOT_HELP_DESCRIPTIONS,
} from "@shared/features/bots/BotForm/common/constants";
import OrderSummary from "features/bots/BotForm/Summaries/OrderSummary";
import SummarySection from "features/bots/BotForm/Summaries/SummarySection";
import { OrderTypes } from "@shared/features/trades/common/types";
import styled from "styled-components";
import useMedia from "utils/hooks/useMedia";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

const OrdersSummary = ({
  title,
  isFetching,
  orders,
  errorMessage,
  showAmount,
  showPrice,
  firstOrderDistanceText,
  priceColumnName,
  amountColumnName,
  orderType,
  botType,
  shouldDisplay,
  setErrorHeight,
  errorsHeight,
  isSideBySide,
  hideAmountColumn,
  sorterHeight,
}: OrdersSummaryProps) => {
  const isMobile = useMedia([`(max-width: 550px)`], [true], false);
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);

  if (!shouldDisplay) return null;

  const isGridBot = botType === BotType.GRID;
  let removeBorderRadiusSide: "left" | "right" | undefined = undefined;
  if (isSideBySide && orderType !== OrderTypes.STOP)
    removeBorderRadiusSide = orderType === OrderTypes.ENTRY ? "right" : "left";
  const spans = [13, 11];
  if (isGridBot) spans.push(6);

  const getAnalyticsDescription = () => {
    let baseDescriptionsMapping =
      botType === BotType.ASAP
        ? DCA_BOT_HELP_DESCRIPTIONS
        : TRADING_VIEW_BOT_HELP_DESCRIPTIONS;
    return baseDescriptionsMapping[orderType];
  };

  const getParsedColumnName = (name: string | string[]) => {
    if (!name) return;
    let nameList: string[];
    if (Array.isArray(name)) nameList = name;
    else nameList = [name];
    return (
      <div>
        {nameList.map((text, index) => (
          <div key={index}>{text}</div>
        ))}
      </div>
    );
  };

  const getPriceColumnName = () =>
    getParsedColumnName(
      priceColumnName ?? t("ordersSummary.columns.price_distance")
    );

  const getErrorMessage = () => {
    return Array.isArray(errorMessage) ? (
      <>
        {errorMessage.map((error) => (
          <div>{error}</div>
        ))}
      </>
    ) : (
      errorMessage
    );
  };

  const columnNames: ReactNode[] = [getPriceColumnName()];
  if (!hideAmountColumn)
    columnNames.push(
      getParsedColumnName(
        amountColumnName || t("ordersSummary.columns.amount_ratio")
      )
    );
  if (botType === BotType.GRID)
    columnNames.push(
      isMobile
        ? t("ordersSummary.columns.immediate_short")
        : t("ordersSummary.columns.is_immediate")
    );

  return (
    <StyledSummarySection
      columnNames={columnNames}
      columnSpans={spans}
      tooltipProps={
        isSideBySide
          ? {
              analyticsFieldName: t(ANALYTICS_FIELD_NAMES[orderType]),
              analyticsPageName: AnalyticsPageName.BOT_FORM,
              description: t(getAnalyticsDescription()),
            }
          : undefined
      }
      title={isSideBySide ? title : undefined}
      isFetching={isFetching}
      errorMessage={getErrorMessage()}
      isCard={false}
      removeBorderRadiusSide={removeBorderRadiusSide}
      setErrorHeight={setErrorHeight}
      errorsHeight={errorsHeight}
      sorterHeight={sorterHeight}
      isSideBySide={isSideBySide}
      $isGridBot={isGridBot}
      $isMobile={isMobile}
    >
      {orders?.map((order, index) => {
        return (
          <OrderSummary
            key={index}
            order={order}
            index={index}
            spans={spans}
            showAmount={showAmount}
            showPrice={showPrice}
            firstOrderDistanceText={firstOrderDistanceText}
            orderType={orderType}
            hideAmountColumn={hideAmountColumn}
            isVerticalCell={isMobile}
          />
        );
      })}
    </StyledSummarySection>
  );
};

export default OrdersSummary;

const StyledSummarySection = styled(SummarySection)<{
  $isGridBot?: boolean;
  $isMobile?: boolean;
}>`
  padding: 0;
  ${(p) =>
    p.$isGridBot &&
    `
    .ant-row {
      flex-wrap: unset !important;
      &:not(:last-of-type) {
        margin-bottom: 6px;
      }
    }
    .ant-col {
      flex-shrink: 1 !important;
      &:last-of-type {
        flex: 0 0 ${p.$isMobile ? 65 : 84}px !important;
      }
    }
  `};
`;
